/**
 * Utility class to draw individual tiles from a packed sprite sheet image.
 *
 * A "packed" spritesheet has tiles placed in a grid with NO spacing.
 * Tile at index `i` is at column `i % cols` and row `Math.floor(i / cols)`.
 * Pixel origin for that tile: `(col * tileW, row * tileH)`.
 */
export class SpriteSheet {
  public readonly image: HTMLImageElement;
  public readonly tileW: number;
  public readonly tileH: number;
  public readonly cols: number;
  public readonly rows: number;

  constructor(image: HTMLImageElement, tileW: number, tileH: number, cols: number, rows: number) {
    this.image = image;
    this.tileW = tileW;
    this.tileH = tileH;
    this.cols = cols;
    this.rows = rows;
  }

  /** Draw a tile by its flat index onto the canvas at (dx, dy) scaled to (dw, dh).
   *  Optional rotation: 0-3 for 0°/90°/180°/270° clockwise. */
  public draw(
    ctx: CanvasRenderingContext2D,
    index: number,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
    rotation: number = 0,
  ): void {
    const col = index % this.cols;
    const row = Math.floor(index / this.cols);
    const sx = col * this.tileW;
    const sy = row * this.tileH;
    if (rotation) {
      ctx.save();
      ctx.translate(dx + dw * 0.5, dy + dh * 0.5);
      ctx.rotate((rotation & 3) * Math.PI * 0.5);
      ctx.drawImage(this.image, sx, sy, this.tileW, this.tileH, -dw * 0.5, -dh * 0.5, dw, dh);
      ctx.restore();
    } else {
      ctx.drawImage(this.image, sx, sy, this.tileW, this.tileH, dx, dy, dw, dh);
    }
  }

  /** Draw a tile by column/row onto the canvas. */
  public drawAt(
    ctx: CanvasRenderingContext2D,
    col: number,
    row: number,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
  ): void {
    const sx = col * this.tileW;
    const sy = row * this.tileH;
    ctx.drawImage(this.image, sx, sy, this.tileW, this.tileH, dx, dy, dw, dh);
  }
}
